import React, { useRef, useState } from 'react';

import { fetchJson } from '../../lib/mathOcrUi';

function Spinner() {
  return <span className="spinner" />;
}

export function BatchUpload({ apiBaseUrl, onJobStarted }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    const pdfs = selected.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) {
      window.alert('Please select PDF files.');
      return;
    }
    setFiles((prev) => [
      ...prev,
      ...pdfs.map((f) => ({ name: f.name, file: f, status: 'pending' })),
    ]);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) {
      window.alert('No new files to upload.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      pendingFiles.forEach((f) => formData.append('file', f.file));
      const res = await fetchJson(`${apiBaseUrl}/api/run`, {
        method: 'POST',
        body: formData,
      });
      const jobId = res.job_id;
      if (onJobStarted) onJobStarted(jobId, pendingFiles.map((f) => f.name));
      // Mark these as processing, keep old completed ones
      const pendingNames = new Set(pendingFiles.map((f) => f.name));
      setFiles((prev) =>
        prev.map((f) => (pendingNames.has(f.name) ? { ...f, status: 'processing' } : f)),
      );
    } catch (e) {
      window.alert(`Upload failed: ${String(e)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = Array.from(e.dataTransfer.files || []);
    const pdfs = dropped.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) return;
    setFiles((prev) => [
      ...prev,
      ...pdfs.map((f) => ({ name: f.name, file: f, status: 'pending' })),
    ]);
  };

  return (
    <div className="batchUpload">
      <div
        className="dropZone"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="dropZoneInner">
          <span className="dropIcon">📁</span>
          <div>Drop PDF files here or click to browse</div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf"
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />
      </div>

      {files.length > 0 && (
        <div className="fileList">
          {files.map((f, i) => (
            <FileRow key={`${f.name}-${i}`} file={f} onRemove={() => removeFile(i)} />
          ))}
        </div>
      )}

      {files.filter((f) => f.status === 'pending').length > 0 && (
        <button
          type="button"
          className="uploadBtn"
          disabled={uploading}
          onClick={handleUpload}
        >
          {uploading ? 'Uploading...' : `Upload ${files.filter((f) => f.status === 'pending').length} file(s)`}
        </button>
      )}
    </div>
  );
}

function FileRow({ file, onRemove }) {
  const icon =
    file.status === 'processing'
      ? '⏳'
      : file.status === 'done'
        ? '✅'
        : file.status === 'error'
          ? '❌'
          : '📄';

  return (
    <div className="fileRow">
      <span className="fileIcon">{icon}</span>
      <span className="fileName">{file.name}</span>
      {file.status === 'processing' && <Spinner />}
      {file.status !== 'processing' && file.status !== 'done' && (
        <button type="button" className="fileRemoveBtn" onClick={onRemove}>
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * Small status panel showing per-file progress in the main view.
 */
export function JobStatusBar({ jobStatus }) {
  if (!jobStatus) return null;

  const files = jobStatus.files || [];
  const results = jobStatus.result || {};
  const statusLabel =
    jobStatus.status === 'processing'
      ? 'Processing'
      : jobStatus.status === 'done'
        ? 'Complete'
        : jobStatus.status === 'error'
          ? 'Error'
          : '';

  const colorClass =
    jobStatus.status === 'processing'
      ? 'statusProcessing'
      : jobStatus.status === 'done'
        ? 'statusDone'
        : 'statusError';

  // Build per-file status from results
  const fileResults = files.map((f) => {
    const stem = f.stem || f.name || '';
    const result = results[stem] || {};
    const hasError = !!result.error;
    // Determine heading: use stem without .pdf for display
    const displayName = (f.name || stem || '').replace(/\\.pdf$/i, '');
    return { displayName, hasError, errorMsg: result.error || '', result };
  });

  // Count errors vs successes
  const errorCount = fileResults.filter((fr) => fr.hasError).length;
  const successCount = fileResults.length - errorCount;

  return (
    <div className={`jobStatusBar ${colorClass}`}>
      <div className="jobStatusHeader">
        <span className="jobStatusLabel">Job {jobStatus.id}: {statusLabel}</span>
        {jobStatus.current_file && (
          <span className="jobCurrentFile">{jobStatus.current_file}</span>
        )}
        {jobStatus.progress && (
          <span className="jobProgress">{jobStatus.progress}</span>
        )}
        {jobStatus.error && (
          <span className="jobError">{jobStatus.error}</span>
        )}
        {fileResults.length > 0 && (
          <span className="jobSummary">
            {successCount > 0 && <span className="jobSuccessCount">{successCount} OK</span>}
            {errorCount > 0 && <span className="jobErrorCount">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>}
          </span>
        )}
      </div>
      {fileResults.length > 0 && (
        <div className="jobFiles">
          {fileResults.map((fr, i) => (
            <span
              key={i}
              className={`jobFileTag${fr.hasError ? ' jobFileTagError' : ' jobFileTagOk'}`}
              title={fr.hasError ? fr.errorMsg : 'Processed successfully'}
            >
              {fr.hasError ? '❌' : '✅'} {fr.displayName}
              {fr.hasError && <span className="jobFileErrorMsg">{fr.errorMsg}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
